import React from 'react';
import styled from 'styled-components';

const BrokersContainer = styled.div`
  padding: 20px 0;
`;

const BrokersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  margin: 40px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
    gap: 25px;
    margin: 30px 0;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin: 25px 0;
  }
`;

const BrokerCard = styled.div`
  background: rgba(18, 34, 64, 0.6);
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 194, 255, 0.2);
  cursor: pointer;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 35px rgba(0, 194, 255, 0.4);
    border-color: rgba(0, 194, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    padding: 25px;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const BrokerHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(0, 194, 255, 0.3);
`;

const BrokerLogo = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00c2ff, #64ffda);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #0c162d;
  font-size: 1.5rem;
  margin-right: 20px;
  box-shadow: 0 5px 15px rgba(0, 194, 255, 0.4);
`;

const BrokerName = styled.h3`
  margin: 0;
  font-size: 1.6rem;
  color: #ffffff;
`;

const BrokerDetails = styled.div`
  margin-bottom: 25px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500;
  color: #8892b0;
`;

const DetailValue = styled.span`
  font-weight: 500;
  color: #c4cdda;
`;

const BrokerDescription = styled.p`
  color: #a8b2d1;
  line-height: 1.7;
  margin: 0 0 20px 0;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  text-align: center;
  margin: 50px 0 30px;
  color: #ffffff;
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #00c2ff, #64ffda);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin: 40px 0 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const Brokers = () => {
  const brokers = [
    {
      id: 1,
      name: "Exness",
      founded: "2008",
      regulation: "FCA, CySEC, FSA",
      headquarters: "London, UK",
      instruments: "Forex, CFDs, Stocks, Indices, Commodities, Cryptocurrencies",
      deposit: "From $1",
      withdrawal: "Instant",
      description: "Exness is a globally recognized broker offering competitive spreads and fast execution. They provide a variety of trading platforms including MetaTrader 4 and 5."
    },
    {
      id: 2,
      name: "XM",
      founded: "2009",
      regulation: "CySEC, ASIC, IFSC",
      headquarters: "Cyprus",
      instruments: "Forex, CFDs, Stocks, Indices, Commodities, Cryptocurrencies, ETFs",
      deposit: "From $5",
      withdrawal: "Instant (varies by method)",
      description: "XM is a well-established broker with a strong reputation for customer service and educational resources. They offer negative balance protection and STP execution."
    },
    {
      id: 3,
      name: "IC Markets",
      founded: "2007",
      regulation: "ASIC, CySEC, FSA",
      headquarters: "Australia",
      instruments: "Forex, CFDs, Stocks, Indices, Commodities, Bonds, Cryptocurrencies",
      deposit: "From $200",
      withdrawal: "Instant (varies by method)",
      description: "IC Markets is known for its tight spreads and fast execution speeds. They offer True ECN trading with direct market access and no dealing desk intervention."
    },
    {
      id: 4,
      name: "iForex",
      founded: "2000",
      regulation: "CySEC, FSCA",
      headquarters: "Israel",
      instruments: "Forex, CFDs, Options, Stocks, Indices, Commodities, Cryptocurrencies",
      deposit: "From $10",
      withdrawal: "Instant (varies by method)",
      description: "iForex is a veteran broker with a comprehensive trading platform and educational resources. They offer a unique trading experience with various account types."
    },
    {
      id: 5,
      name: "OctaFX",
      founded: "2011",
      regulation: "CySEC, FSA",
      headquarters: "Cyprus",
      instruments: "Forex, CFDs, Stocks, Indices, Commodities, Cryptocurrencies",
      deposit: "From $1",
      withdrawal: "Instant (varies by method)",
      description: "OctaFX offers competitive trading conditions with a focus on transparency. They provide various account types including ECN and cTrader accounts."
    },
    {
      id: 6,
      name: "Pepperstone",
      founded: "2010",
      regulation: "FCA, ASIC, CySEC",
      headquarters: "Australia",
      instruments: "Forex, CFDs, Stocks, Indices, Commodities, ETFs, Cryptocurrencies",
      deposit: "From $200",
      withdrawal: "Instant (varies by method)",
      description: "Pepperstone is an award-winning broker with a strong focus on technology and innovation. They offer STP execution and competitive spreads on multiple platforms."
    },
    {
      id: 7,
      name: "FBS",
      founded: "2009",
      regulation: "IFSC, CySEC",
      headquarters: "Belize",
      instruments: "Forex, CFDs, Stocks, Indices, Commodities, Cryptocurrencies",
      deposit: "From $1",
      withdrawal: "Instant (varies by method)",
      description: "FBS is known for its promotional offers and educational resources. They provide multiple account types with different features to suit various traders."
    },
    {
      id: 8,
      name: "FP Markets",
      founded: "2005",
      regulation: "ASIC, FCA, CySEC",
      headquarters: "Australia",
      instruments: "Forex, CFDs, Stocks, Indices, Commodities, Cryptocurrencies",
      deposit: "From $100",
      withdrawal: "Instant (varies by method)",
      description: "FP Markets offers competitive trading conditions with a focus on ECN trading. They provide low-latency execution and a variety of trading platforms."
    }
  ];

  const brokerTypes = [
    {
      title: "Regulated Brokers",
      description: "These brokers are regulated by top-tier financial authorities, ensuring client fund safety and fair trading practices."
    },
    {
      title: "Instant Withdrawal Brokers",
      description: "These brokers offer fast withdrawal processing, allowing you to access your funds quickly when needed."
    },
    {
      title: "STP/ECN Brokers",
      description: "These brokers provide direct market access with transparent pricing and fast execution speeds."
    }
  ];

  return (
    <BrokersContainer>
      <SectionTitle>Trusted Brokers</SectionTitle>
      <p style={{ textAlign: 'center', color: '#a8b2d1', marginBottom: '30px', fontSize: '1.1rem', maxWidth: '800px', margin: '0 auto 40px' }}>
        We've partnered with only the most reliable and regulated brokers in the industry. 
        All brokers listed below offer secure trading environments, competitive conditions, and excellent customer support.
      </p>
      
      {brokerTypes.map((type, index) => (
        <div key={index} style={{ marginBottom: '40px', textAlign: 'center' }}>
          <h3 style={{ fontSize: '1.5rem', color: '#ffffff', marginBottom: '15px' }}>{type.title}</h3>
          <p style={{ color: '#a8b2d1', marginBottom: '20px', maxWidth: '800px', margin: '0 auto' }}>{type.description}</p>
        </div>
      ))}
      
      <BrokersGrid>
        {brokers.map((broker) => (
          <BrokerCard key={broker.id}>
            <BrokerHeader>
              <BrokerLogo>{broker.name.charAt(0)}</BrokerLogo>
              <BrokerName>{broker.name}</BrokerName>
            </BrokerHeader>
            
            <BrokerDetails>
              <DetailRow>
                <DetailLabel>Founded:</DetailLabel>
                <DetailValue>{broker.founded}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Regulation:</DetailLabel>
                <DetailValue>{broker.regulation}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Headquarters:</DetailLabel>
                <DetailValue>{broker.headquarters}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Instruments:</DetailLabel>
                <DetailValue>{broker.instruments}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Min. Deposit:</DetailLabel>
                <DetailValue>{broker.deposit}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Withdrawal:</DetailLabel>
                <DetailValue>{broker.withdrawal}</DetailValue>
              </DetailRow>
            </BrokerDetails>
            
            <BrokerDescription>{broker.description}</BrokerDescription>
          </BrokerCard>
        ))}
      </BrokersGrid>
    </BrokersContainer>
  );
};

export default Brokers;